import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { House, PersonSimpleWalk, Buildings } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useSeo, breadcrumbs, offerList, localBusiness } from "@/lib/seo";
import { PublicShell, Crumbs } from "@/components/PublicShell";
import { ShareBar } from "@/components/ShareBar";
import { NotifyMe } from "@/components/NotifyMe";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function HousingPublic() {
  const [board, setBoard] = useState(null);

  useEffect(() => {
    api.get("/workplace/public").then(({ data }) => setBoard(data)).catch(() => setBoard({ homes: [] }));
  }, []);

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      breadcrumbs([["Home", "/"], ["Living near work", "/housing"]]),
      localBusiness({
        name: "Unity & Utility — homes near work",
        description: "Flats, houses, rooms in a share, caravans and lodges to let within a few miles of registered workplace gates, with the walk and cycle time to the gate.",
        path: "/housing",
        image: board?.image_url ? `${BACKEND}${board.image_url}` : undefined,
        type: "RealEstateAgent",
        area: "Worldwide",
      }),
      offerList("To let near the gate", "/housing", (board?.homes || []).map((h) => ({
        name: h.title,
        description: [`${h.kind}`, `${h.rooms} room(s)`, h.furnishing,
                      h.parking ? "parking" : "no parking",
                      h.miles_from_gate != null
                        ? `${h.miles_from_gate} miles from ${h.site_name || "the gate"}, ${h.walk_minutes} min walk`
                        : null].filter(Boolean).join(", "),
        price: h.rent_per_month, symbol: h.symbol, unit: "per month",
      }))),
    ],
  };

  useSeo({
    title: "Rooms, flats and houses to let near your workplace — walk or cycle to the gate | Unity & Utility",
    description: "Apartments, houses, rooms in a share, caravans and lodges to let within a few miles of registered workplaces, anywhere in the world, with the walk and cycle time to the gate and the employer's published rate for a shared lift.",
    path: "/housing",
    image: board?.image_url ? `${BACKEND}${board.image_url}` : undefined,
    jsonLd,
  });

  return (
    <PublicShell>
      <div className="mx-auto max-w-5xl px-5 py-10 sm:px-8 sm:py-14" data-testid="housing-public-page">
        <Crumbs items={[["Home", "/"], ["Living near work", "/housing"]]} />
        <p className="overline text-[#B3241B]">Living near work</p>
        <h1 className="font-display mt-2 flex items-start gap-3 text-4xl font-bold leading-[1.05] tracking-tight text-[#0F172A] sm:text-5xl">
          <House size={34} weight="duotone" className="mt-1 shrink-0 text-[#002FA7]" />
          Near enough to walk in
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-700" data-testid="housing-public-intro">
          Flats, houses, rooms in a share, studios, caravans and lodges to let within a few miles
          of registered workplaces, anywhere in the world. Employers publish the least a colleague
          may be paid for a lift to the gate, and owners list what is free nearby — because the
          cure for a chaotic car park is not more tarmac, it is living near enough to walk, cycle
          or share a ride in.
        </p>
        <div className="mt-5">
          <ShareBar
            url={board?.share_token
              ? `${window.location.origin}/p/${board.share_token}`
              : `${window.location.origin}/housing`}
            text="Places to let within walking distance of work — less commute, less parking, more of your life."
            testid="housing-public-share" instagram marketplace />
        </div>

        {board?.image_url && (
          <img src={`${BACKEND}${board.image_url}`}
            alt="Houses within sight of a works chimney, people walking to the gate at dawn"
            loading="lazy" className="mt-8 h-56 w-full border border-border object-cover sm:h-80"
            data-testid="housing-public-image" />
        )}

        <section className="mt-6 border border-[#0B5C2E] bg-[#F3FAF5] p-5" data-testid="housing-public-card">
          <p className="overline text-[#0B5C2E]">
            Today{board?.day ? ` · ${board.day}` : ""}
            {board?.auto_posted && <span className="ml-2">Posted to Facebook</span>}
          </p>
          <div className="mt-3 grid grid-cols-3 gap-3">
            {[["Places to let", board?.homes_count, "housing-stat-homes"],
              ["Walkable", board?.walkable, "housing-stat-walkable"],
              ["Workplaces", (board?.sites || []).length, "housing-stat-sites"]].map(([l, v, t]) => (
              <div key={t} className="border border-border bg-white p-3" data-testid={t}>
                <p className="font-display text-2xl font-bold text-[#0F172A]">{v ?? 0}</p>
                <p className="text-xs text-slate-500">{l}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-8" data-testid="housing-public-sites">
          <h2 className="font-display text-base font-bold tracking-tight text-[#0F172A] md:text-lg">
            Registered workplaces and their published ride rate
          </h2>
          {board?.section_images?.sites && (
            <img src={`${BACKEND}${board.section_images.sites}`} loading="lazy"
              alt="A factory gate at shift change with workers walking in on foot"
              className="mt-3 h-44 w-full border border-border object-cover sm:h-56"
              data-testid="housing-public-sites-image" />
          )}
          {(board?.sites || []).length === 0 ? (
            <p className="mt-2 text-sm text-slate-600" data-testid="housing-public-sites-empty">
              No workplaces registered round here yet. Employers: register the gate, publish the
              least a colleague may be paid for a lift, and set how far out the board may look
              for homes.
            </p>
          ) : (
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {board.sites.map((s, i) => (
                <div key={i} className="border border-border bg-white p-4" data-testid={`housing-public-site-${i}`}>
                  <p className="text-sm font-semibold text-[#0F172A]">
                    <Buildings size={14} className="mr-1 inline align-[-2px] text-[#002FA7]" />{s.name}
                  </p>
                  <p className="text-xs text-slate-500">{s.gate} · {s.radius_miles} mile radius · {s.people} registered</p>
                  <p className="mt-1 text-sm text-slate-700">
                    Published rate: {s.symbol}{Number(s.min_per_mile || 0).toFixed(2)} a mile ·
                    {" "}{s.symbol}{Number(s.min_per_seat || 0).toFixed(2)} a seat
                  </p>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="mt-8" data-testid="housing-public-homes">
          <h2 className="font-display text-base font-bold tracking-tight text-[#0F172A] md:text-lg">
            To let near the gate
          </h2>
          {board?.section_images?.homes && (
            <img src={`${BACKEND}${board.section_images.homes}`} loading="lazy"
              alt="A bright flat ready for a new tenant with a works chimney through the window"
              className="mt-3 h-44 w-full border border-border object-cover sm:h-56"
              data-testid="housing-public-homes-image" />
          )}
          {(board?.homes || []).length === 0 ? (
            <p className="mt-2 text-sm text-slate-600" data-testid="housing-public-homes-empty">
              Nothing on the board here yet. Owners and landlords: list the flat, the room, the
              house, the caravan or the lodge, and workers at the nearby gates will see it.
            </p>
          ) : (
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              {board.homes.map((h, i) => (
                <div key={i} className="border border-border bg-white p-4" data-testid={`housing-public-home-${i}`}>
                  <p className="text-sm font-semibold text-[#0F172A]">{h.title}</p>
                  <p className="text-xs text-slate-500">
                    {h.kind} · {h.rooms} room(s) · {h.furnishing}
                    {h.parking ? " · parking" : " · no parking"}
                    {h.site_name ? ` · ${h.site_name}` : ""}
                  </p>
                  {h.miles_from_gate != null && (
                    <p className="mt-1 text-xs text-[#0B5C2E]">
                      <PersonSimpleWalk size={12} className="mr-1 inline align-[-2px]" />
                      {h.miles_from_gate} miles from the gate · {h.walk_minutes} min walk · {h.cycle_minutes} min cycle
                    </p>
                  )}
                  <p className="mt-1 text-sm font-semibold text-[#0B5C2E]">
                    {h.symbol}{Number(h.rent_per_month || 0).toFixed(0)} a month · free from {h.free_from}
                  </p>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="mt-8" data-testid="housing-public-commute">
          <h2 className="font-display text-base font-bold tracking-tight text-[#0F172A] md:text-lg">
            Walk it, cycle it, or share the ride
          </h2>
          {board?.section_images?.commute && (
            <img src={`${BACKEND}${board.section_images.commute}`} loading="lazy"
              alt="A worker walking the short lane home with the works site behind"
              className="mt-3 h-44 w-full border border-border object-cover sm:h-56"
              data-testid="housing-public-commute-image" />
          )}
          <p className="mt-2 text-sm leading-relaxed text-slate-600">
            Live within the radius and the car can stay at home. Where it cannot, colleagues
            share the run at the rate the employer has published — no haggling at the gate.
          </p>
        </section>

        <NotifyMe source="housing" testid="housing-notify" />

        <section className="mt-10 border border-border bg-white p-5" data-testid="housing-public-join">
          <p className="overline text-[#002FA7]">Employers, owners and workers</p>
          <p className="mt-2 text-sm leading-relaxed text-slate-700">
            Employers register the site and publish the rate. Landlords and property owners list
            what is to let within the radius and get approved. Workers say their budget, their
            shifts and whether they have a car, and the board picks what suits.
          </p>
          <Link to="/register" className="mt-3 inline-block text-sm font-semibold text-[#002FA7] underline"
            data-testid="housing-public-register">
            Register and put yours up
          </Link>
        </section>
      </div>
    </PublicShell>
  );
}
